// AdminBots — Bot moderation & marketplace management
// Approve, suspend, takedown, view subscribers, ban author

import { useState } from 'react';

interface BotMod {
  id: string; name: string; emoji: string; color: string; author: string; authorId: string;
  category: string; tier: string; subs: number; price: number; status: 'active' | 'pending_review' | 'suspended' | 'takedown';
  flags: string[]; reports: number;
}

const BOTS: BotMod[] = [
  { id: 'b1', name: 'Gold Digger',     emoji: '🥇', color: '#FF9500', author: 'Marcus Chen',  authorId: 'u2', category: 'Breakout',       tier: 'Beginner',     subs: 8420, price: 0,  status: 'active',          flags: [], reports: 0 },
  { id: 'b2', name: 'NQ Sniper',       emoji: '🎯', color: '#FF3B30', author: 'Aiden Wright', authorId: 'u4', category: 'Sniper',         tier: 'Professional', subs: 5340, price: 29, status: 'active',          flags: [], reports: 2 },
  { id: 'b3', name: 'ES Mean Reverter', emoji: '🔁', color: '#007AFF', author: 'Sofia Petrov', authorId: 'u3', category: 'Mean Reversion', tier: 'Mid',          subs: 3210, price: 0,  status: 'active',          flags: [], reports: 0 },
  { id: 'b4', name: 'CryptoScamX',     emoji: '🪙', color: '#FF3B30', author: 'Anonymous',     authorId: 'u99',category: 'Trend',          tier: 'Beginner',     subs: 0,   price: 99, status: 'pending_review',  flags: ['Suspicious PnL', 'Hidden fees'], reports: 14 },
  { id: 'b5', name: 'Scalper Supreme', emoji: '⚡', color: '#34C759', author: 'Yuki Tanaka',  authorId: 'u7', category: 'Scalper',        tier: 'Beginner',     subs: 9120, price: 0,  status: 'active',          flags: [], reports: 1 },
  { id: 'b6', name: 'PumpKing',        emoji: '📈', color: '#FF9500', author: 'fakeuser',      authorId: 'u77',category: 'Breakout',       tier: 'Professional', subs: 12,  price: 199,status: 'suspended',       flags: ['Misleading claims', 'Suspicious returns'], reports: 28 },
];

export function AdminBots() {
  const [bots, setBots] = useState(BOTS);
  const [filter, setFilter] = useState<'all' | 'pending' | 'flagged' | 'suspended'>('all');

  const filtered = bots.filter((b) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return b.status === 'pending_review';
    if (filter === 'flagged') return b.flags.length > 0 || b.reports > 0;
    if (filter === 'suspended') return b.status === 'suspended';
    return true;
  });

  function approve(id: string) { setBots((c) => c.map((b) => b.id === id ? { ...b, status: 'active' } : b)); alert('Bot approved & published'); }
  function suspend(id: string) { setBots((c) => c.map((b) => b.id === id ? { ...b, status: 'suspended' } : b)); alert('Bot suspended. Author notified.'); }
  function takedown(id: string) { setBots((c) => c.filter((b) => b.id !== id)); alert('Bot removed from marketplace. Author banned.'); }
  function banAuthor(authorId: string) { if (confirm('Ban this author? All their bots will be removed.')) { setBots((c) => c.filter((b) => b.authorId !== authorId)); alert('Author banned.'); } }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-[22px] font-bold text-white">Bots marketplace</h1>
          <p className="text-[12px] text-white/50">{bots.length} bots · {bots.filter((b) => b.status === 'pending_review').length} pending review</p>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {(['all', 'pending', 'flagged', 'suspended'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`botfilter-${f}`} className={['rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition', filter === f ? 'bg-white text-black' : 'bg-white/5 text-white/60 active:bg-white/10'].join(' ')}>
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map((b) => (
          <div key={b.id} data-testid={`botmod-${b.id}`} className={['rounded-[16px] bg-[#1C1C1E] border p-4', b.flags.length > 0 ? 'border-[#FF3B30]/40' : 'border-white/5'].join(' ')}>
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-[12px] text-[24px]" style={{ backgroundColor: b.color + '20' }}>{b.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[15px] font-bold text-white">{b.name}</span>
                  <span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                    b.status === 'active' ? 'bg-[#34C759]/20 text-[#34C759]' :
                    b.status === 'pending_review' ? 'bg-[#FF9500]/20 text-[#FF9500]' :
                    b.status === 'suspended' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                    'bg-white/10 text-white/60'].join(' ')}>{b.status.replace('_', ' ')}</span>
                  <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white/60">{b.category}</span>
                  <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white/60">{b.tier}</span>
                </div>
                <div className="text-[12px] text-white/50">by {b.author} · {b.subs.toLocaleString()} subscribers · {b.price === 0 ? 'Free' : `$${b.price}/mo`}</div>
                {b.flags.length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {b.flags.map((f) => <div key={f} className="text-[10px] text-[#FF3B30]">🚩 {f}</div>)}
                    {b.reports > 0 && <div className="text-[10px] text-[#FF3B30]">⚠ {b.reports} user reports</div>}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1.5 items-end">
                {b.status === 'pending_review' && <button onClick={() => approve(b.id)} data-testid={`bot-approve-${b.id}`} className="h-8 rounded-full bg-[#34C759] px-3 text-[11px] font-semibold text-white active:opacity-40">Approve</button>}
                {b.status === 'active' && <button onClick={() => suspend(b.id)} data-testid={`bot-suspend-${b.id}`} className="h-8 rounded-full bg-[#FF9500] px-3 text-[11px] font-semibold text-white active:opacity-40">Suspend</button>}
                {b.status === 'suspended' && <button onClick={() => approve(b.id)} className="h-8 rounded-full bg-[#34C759] px-3 text-[11px] font-semibold text-white active:opacity-40">Reinstate</button>}
                <button onClick={() => takedown(b.id)} data-testid={`bot-takedown-${b.id}`} className="h-8 rounded-full bg-[#FF3B30] px-3 text-[11px] font-semibold text-white active:opacity-40">Take down</button>
                <button onClick={() => banAuthor(b.authorId)} data-testid={`bot-banauthor-${b.id}`} className="h-8 rounded-full bg-white/5 px-3 text-[11px] font-semibold text-white/60 active:bg-white/10">Ban author</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
